import { useState, useEffect, useMemo } from 'react';
import { Calendar, MapPin, Clock, Search, ExternalLink, Sparkles, AlertTriangle, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { SEOPage } from '@/components/SEO';
import { getActiveSpotlights } from '@/lib/api';

const HERO_BG = "https://customer-assets.emergentagent.com/job_ae021ef0-7027-4ae5-b97f-cd7f1827f457/artifacts/z1lv2e2v_P2341000.JPG";
const FEED_URL = "https://paisley.org.uk/api/events/feed?limit=100";
const HUB_URL = "https://paisley.org.uk/events";

const formatDate = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  return {
    day: d.getDate(),
    month: d.toLocaleDateString('en-GB', { month: 'short' }),
    year: d.getFullYear(),
    full: d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }),
  };
};

const EventsPage = () => {
  const [events, setEvents] = useState([]);
  const [spotlights, setSpotlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');

  useEffect(() => {
    // No caching — fetch live on every page load
    let cancelled = false;
    (async () => {
      try {
        const [feedRes, spotlightRes] = await Promise.all([
          fetch(FEED_URL, { method: 'GET', cache: 'no-store' }),
          getActiveSpotlights().catch(() => ({ data: [] })),
        ]);
        if (!feedRes.ok) throw new Error(`Feed responded ${feedRes.status}`);
        const data = await feedRes.json();
        if (cancelled) return;
        setEvents(Array.isArray(data) ? data : []);
        setSpotlights(spotlightRes.data || []);
        setErrored(false);
      } catch {
        if (!cancelled) setErrored(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const categories = useMemo(() => {
    const list = [];
    for (const e of events) {
      const c = (e.category || '').trim();
      if (c && !list.includes(c)) list.push(c);
    }
    return ['all', ...list];
  }, [events]);

  const filteredEvents = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return events.filter(e => {
      const matchesSearch = !q ||
        (e.title || '').toLowerCase().includes(q) ||
        (e.venue || '').toLowerCase().includes(q);
      const matchesCategory = categoryFilter === 'all' || e.category === categoryFilter;
      return matchesSearch && matchesCategory;
    });
  }, [events, searchTerm, categoryFilter]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="events-page">
      <SEOPage.Events />
      <Navbar />

      {/* Hero */}
      <section
        className="relative pt-32 pb-20"
        style={{ backgroundImage: `url(${HERO_BG})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/90 to-slate-900/95" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="mb-4 bg-sky-500/20 text-sky-400 border-sky-500/30">
            <Calendar className="h-3 w-3 mr-1" /> What's On
          </Badge>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
            Events in Paisley
          </h1>
          <p className="text-slate-300 text-lg max-w-2xl mx-auto">
            Live event listings mirrored from Paisley.org.uk — the local event hub for Paisley & Renfrewshire.
          </p>
        </div>
      </section>

      {/* Spotlights (separate product — kept distinct from the live feed) */}
      {spotlights.length > 0 && (
        <section className="py-12 bg-gradient-to-b from-amber-50 to-transparent" data-testid="spotlights-strip">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="h-5 w-5 text-amber-500" />
              <h2 className="font-heading text-xl font-semibold text-slate-900">Featured Spotlights</h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {spotlights.slice(0, 3).map(s => (
                <Card key={s.id} className="border-amber-200 bg-white" data-testid={`spotlight-card-${s.id}`}>
                  <CardContent className="p-5">
                    <Badge className="mb-2 bg-amber-100 text-amber-700 border-amber-200">Spotlight</Badge>
                    <h3 className="font-semibold text-slate-900 mb-1 line-clamp-2">{s.event_title || s.title}</h3>
                    <p className="text-sm text-slate-600 mb-3 line-clamp-2">{s.event_description || s.description}</p>
                    {(s.event_link || s.link) && (
                      <a href={s.event_link || s.link} target="_blank" rel="noopener noreferrer">
                        <Button variant="outline" size="sm" className="border-amber-200 text-amber-700 hover:bg-amber-50">
                          More info <ExternalLink className="h-3 w-3 ml-1" />
                        </Button>
                      </a>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Filter Bar */}
      <section className="py-8 bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search events…"
              className="pl-9"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              data-testid="events-search-input"
            />
          </div>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="md:w-56" data-testid="events-category-filter">
              <SelectValue placeholder="All categories" />
            </SelectTrigger>
            <SelectContent>
              {categories.map(c => (
                <SelectItem key={c} value={c}>
                  {c === 'all' ? 'All categories' : c.charAt(0).toUpperCase() + c.slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </section>

      {/* Events Grid */}
      <section className="py-16" data-testid="events-grid-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1,2,3,4,5,6].map(i => (
                <Card key={i} className="animate-pulse">
                  <CardContent className="p-6">
                    <div className="h-5 bg-slate-200 rounded w-3/4 mb-3" />
                    <div className="h-4 bg-slate-100 rounded w-1/2 mb-2" />
                    <div className="h-4 bg-slate-100 rounded w-2/3" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : errored ? (
            <Card className="max-w-2xl mx-auto" data-testid="events-fallback">
              <CardContent className="p-10 text-center">
                <AlertTriangle className="h-10 w-10 text-amber-400 mx-auto mb-4" />
                <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">
                  Live events temporarily unavailable
                </h3>
                <p className="text-slate-600 mb-6">
                  See the full list at{' '}
                  <a href={HUB_URL} className="text-sky-600 hover:text-sky-700 font-medium underline">
                    paisley.org.uk/events
                  </a>.
                </p>
                <a href={HUB_URL}>
                  <Button className="bg-sky-500 hover:bg-sky-600 text-white" data-testid="events-fallback-btn">
                    View events on Paisley.org.uk <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </a>
              </CardContent>
            </Card>
          ) : filteredEvents.length === 0 ? (
            <div className="text-center py-16">
              <Calendar className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">No events match your filters</h3>
              <p className="text-slate-500">Try clearing the search or category filter.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredEvents.map((event, i) => {
                const d = formatDate(event.date);
                return (
                  <a
                    key={`${event.url || event.title}-${i}`}
                    href={event.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group block"
                    data-testid={`event-card-${i}`}
                  >
                    <Card className="h-full hover:shadow-xl transition-all hover:-translate-y-1 overflow-hidden">
                      <CardContent className="p-0">
                        <div className="flex gap-4 p-5">
                          {d && (
                            <div className="flex-shrink-0 w-16 text-center bg-sky-50 rounded-lg p-2 border border-sky-100">
                              <p className="text-xs text-sky-600 font-medium uppercase">{d.month}</p>
                              <p className="text-2xl font-bold text-slate-900 leading-none mt-1">{d.day}</p>
                              <p className="text-[10px] text-slate-400 mt-1">{d.year}</p>
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            {event.category && (
                              <Badge variant="outline" className="text-[10px] mb-2 capitalize">
                                {event.category}
                              </Badge>
                            )}
                            <h3 className="font-heading text-base font-semibold text-slate-900 mb-2 line-clamp-2 group-hover:text-sky-600 transition-colors">
                              {event.title}
                            </h3>
                            {event.venue && (
                              <p className="text-sm text-slate-600 flex items-center gap-1 mb-1">
                                <MapPin className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                                <span className="truncate">{event.venue}</span>
                              </p>
                            )}
                            {event.time && (
                              <p className="text-sm text-slate-500 flex items-center gap-1">
                                <Clock className="h-3.5 w-3.5 text-slate-400" />
                                {event.time}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="border-t border-slate-100 px-5 py-3 bg-slate-50 flex items-center justify-between text-xs text-slate-500">
                          <span>View on Paisley.org.uk</span>
                          <ExternalLink className="h-3.5 w-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                        </div>
                      </CardContent>
                    </Card>
                  </a>
                );
              })}
            </div>
          )}

          {/* Attribution */}
          {!loading && !errored && (
            <p className="text-center text-sm text-slate-500 mt-10" data-testid="events-attribution">
              Events provided by{' '}
              <a
                href="https://paisley.org.uk"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sky-600 hover:text-sky-700 underline font-medium"
              >
                Paisley.org.uk
              </a>{' '}
              — the local event hub for Paisley & Renfrewshire.
            </p>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-sky-500" data-testid="events-cta-section">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
            Got an event to promote?
          </h2>
          <p className="text-sky-100 mb-6">
            Boost any event with a Featured Spotlight, or list it on Paisley.org.uk to appear here automatically.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a href="/spotlight">
              <Button size="lg" className="bg-white text-sky-600 hover:bg-sky-50 px-8">
                <Sparkles className="h-4 w-4 mr-2" /> Spotlight an Event
              </Button>
            </a>
            <a href={HUB_URL} target="_blank" rel="noopener noreferrer">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8">
                Submit to Paisley.org.uk <ExternalLink className="h-4 w-4 ml-2" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EventsPage;
